import notifyOrphanedInterview from '@Application/ApplicationSettings/DevicePorts/notifyOrphanedInterview';
import readSubjectAddressFromContext from '@Application/Subjects/readSubjectAddressFromContext';
import type { OutboxItemState } from '@Domain/Ports/Outbox/OutboxPort';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useDeviceNotificationPort from '@UI/Providers/PortsProvider/Hooks/Notification/useDeviceNotificationPort';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import * as React from 'react';

/**
 * Renders nothing: tells the user, through the device, the moment a transfer of this session finds
 * its interview deleted. Only the transition `'transferring'` to `'orphaned'` is announced, so an
 * item that was already waiting for a decision when the application opened is left to its banner.
 */
const OrphanedTransferNotifier: React.FC = () => {
	const outboxItems = useOutboxItems();
	const notificationPort = useDeviceNotificationPort();
	const previousStates = React.useRef<ReadonlyMap<string, OutboxItemState>>(new Map());

	React.useEffect(() => {
		for (const item of outboxItems) {
			const localInterviewId = item.context.localInterviewId;
			const subject = readSubjectAddressFromContext(item.context);

			if (
				item.state === 'orphaned' &&
				previousStates.current.get(item.id) === 'transferring' &&
				typeof localInterviewId === 'string' &&
				subject !== null
			) {
				notifyOrphanedInterview(notificationPort, {
					subjectPath: buildSubjectPath(subject),
					localInterviewId,
				}).catch(() => {
					// The banner remains: a notification that cannot be shown is not an error to surface.
				});
			}
		}

		previousStates.current = new Map(outboxItems.map((item) => [item.id, item.state]));
	}, [outboxItems, notificationPort]);

	return null;
};

export default OrphanedTransferNotifier;
